import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DailyProvider } from '@daily-co/daily-react';
import type { DailyCall } from '@daily-co/daily-js';
import DailyCallManager from '@/utils/dailyCallManager';
import {
  Loader,
  Center,
  Box,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '@/stores/authStore';
import { useRoomStore } from '@/stores/roomStore';
import { useConversaStore } from '@/stores/conversaStore';
import { useAgentStore } from '@/stores/agentStore';
import { roomsApi } from '@/services/api.service';
import { MeetingRoomContent } from './MeetingRoomContent';
import { ConversaSidebar } from '@/components/ConversaSidebar';
import { CopilotSidebar } from '@/components/CopilotSidebar';
import { MeetingEndingModal } from '@/components/MeetingEndingModal';
import { useAgentWebSocket } from '@/hooks/useAgentWebSocket';
import { useChat } from '@/hooks/useChat';
import { useTranscription } from '@/hooks/useTranscription';

export const MeetingRoom = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const currentRoom = useRoomStore((state) => state.currentRoom);
  const setRoom = useRoomStore((state) => state.setRoom);
  const loadConversationHistory = useConversaStore((state) => state.loadHistory);
  const clearConversation = useConversaStore((state) => state.clearMessages);

  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Agent WebSocket - only enabled for room owner AND when room is properly loaded
  const isOwner = currentRoom?.isOwner || false;
  const isRoomLoaded = currentRoom && currentRoom.code === code?.toUpperCase();
  const {
    isConnected: isAgentConnected,
    sendChunk: sendAgentChunk,
    endMeeting: endAgentMeeting,
  } = useAgentWebSocket({
    roomCode: code?.toUpperCase() || '',
    enabled: isOwner && !!code && !!isRoomLoaded && !loading,
  });

  // Callback to send transcription segments to agent
  const handleTranscriptionSegment = useCallback(
    (text: string) => {
      if (isOwner && isAgentConnected) {
        sendAgentChunk(text);
      }
    },
    [isOwner, isAgentConnected, sendAgentChunk]
  );

  // Callback to send chat messages to agent (same as transcription)
  const handleChatMessage = useCallback(
    (text: string) => {
      if (isOwner && isAgentConnected) {
        sendAgentChunk(text);
      }
    },
    [isOwner, isAgentConnected, sendAgentChunk]
  );

  // Handle end meeting
  const handleEndMeeting = useCallback(() => {
    if (isOwner && isAgentConnected) {
      endAgentMeeting();
    }
  }, [isOwner, isAgentConnected, endAgentMeeting]);

  useEffect(() => {
    let isMounted = true;

    const initRoom = async () => {
      if (!code || !user) {
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get room info
        let room = currentRoom;
        if (!room || room.code !== code.toUpperCase()) {
          console.log('Fetching room info for code:', code);
          room = await roomsApi.getRoom(code.toUpperCase());
          if (!isMounted) return;
          setRoom(room);
        }

        if (!isMounted) return;

        // Load conversation history for this room
        await loadConversationHistory(code.toUpperCase());

        // Get or create Daily call object using singleton manager
        const daily = await DailyCallManager.getCallObject(room.roomUrl);

        if (isMounted) {
          setCallObject(daily);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error initializing room:', err);
        if (!isMounted) return;

        const errorMsg = err.response?.status === 404
          ? 'Sala não encontrada ou expirada'
          : 'Erro ao carregar a sala';

        setError(errorMsg);
        setLoading(false);

        notifications.show({
          title: 'Erro',
          message: errorMsg,
          color: 'red',
        });

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          if (isMounted) {
            navigate('/dashboard');
          }
        }, 2000);
      }
    };

    initRoom();

    // Cleanup: Don't destroy on unmount as it might be reused
    // The manager will handle cleanup when navigating to a different room
    return () => {
      isMounted = false;
      // Clear conversation when leaving the room
      clearConversation();
    };
  }, [code, user?.id, loadConversationHistory, clearConversation]);

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Box style={{ textAlign: 'center' }}>
          <Loader size="lg" mb="md" />
          <Text>Carregando sala...</Text>
        </Box>
      </Center>
    );
  }

  if (error || !callObject) {
    return (
      <Center style={{ height: '100vh' }}>
        <Box style={{ textAlign: 'center' }}>
          <Text size="xl" fw={600} mb="md">
            {error || 'Erro ao carregar sala'}
          </Text>
          <Text c="dimmed">Redirecionando para o dashboard...</Text>
        </Box>
      </Center>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <MeetingRoomLayout
        roomCode={code!.toUpperCase()}
        isOwner={isOwner}
        isAgentConnected={isAgentConnected}
        onEndMeeting={handleEndMeeting}
        onTranscriptionSegment={handleTranscriptionSegment}
        onChatMessage={handleChatMessage}
      />
    </DailyProvider>
  );
};

// Internal component that uses Daily hooks (must be inside DailyProvider)
interface MeetingRoomLayoutProps {
  roomCode: string;
  isOwner: boolean;
  isAgentConnected: boolean;
  onEndMeeting: () => void;
  onTranscriptionSegment: (text: string) => void;
  onChatMessage: (text: string) => void;
}

const MeetingRoomLayout = ({
  roomCode,
  isOwner,
  isAgentConnected,
  onEndMeeting,
  onTranscriptionSegment,
  onChatMessage,
}: MeetingRoomLayoutProps) => {
  const { sendMessage } = useChat();
  const meetingEndingCountdown = useAgentStore((state) => state.meetingEndingCountdown);

  // Wrap sendMessage to also send to agent
  const handleSendChatMessage = useCallback((text: string) => {
    sendMessage(text);
    onChatMessage(text);
  }, [sendMessage, onChatMessage]);

  // Initialize transcription hook
  const { isTranscribing, startTranscription, stopTranscription } = useTranscription({
    roomCode,
    isOwner,
    onSegmentComplete: onTranscriptionSegment,
  });

  return (
    <>
      {/* Meeting ending countdown modal */}
      <MeetingEndingModal countdown={meetingEndingCountdown} />
    <Box
      style={{
        display: 'flex',
        height: 'calc(100vh - 60px)',
        backgroundColor: '#1a1a1a',
      }}
    >
      {/* Left sidebar - Conversa */}
      <ConversaSidebar
        onSendChatMessage={handleSendChatMessage}
        isOwner={isOwner}
        isTranscribing={isTranscribing}
        onStartTranscription={startTranscription}
        onStopTranscription={stopTranscription}
      />

      {/* Main content area - Video */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <MeetingRoomContent roomCode={roomCode} isOwner={isOwner} />
      </Box>

      {/* Right sidebar - Copilot (only for owner) */}
      {isOwner && (
        <CopilotSidebar
          roomCode={roomCode}
          onEndMeeting={onEndMeeting}
          isConnected={isAgentConnected}
        />
      )}
    </Box>
    </>
  );
};
